import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import "./ProductDetailPage.css";
import { useCartStore } from "../stores/cartStore";
import CheckoutButton from "./CheckoutButton";
import { urlFor } from "../sanityClient";
import LoadingScreen from "./LoadingScreen"; // <--- Ensures we use the global loader

function ProductDetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCartStore();

  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  const [pricing, setPricing] = useState({
    price: null,
    currency: "CAD",
    stripePriceId: null,
  });
  const [pricingLoading, setPricingLoading] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    (async () => {
      try {
        setStatus("loading");
        const res = await fetch(
          `/.netlify/functions/get-product-by-slug?slug=${encodeURIComponent(slug)}`
        );
        if (!res.ok) throw new Error("Product not found.");
        const data = await res.json();

        if (!cancelled) {
          setProduct(data || null);
          setPricing({
            price: typeof data?.price === "number" ? data.price : null,
            currency: data?.currency || "CAD",
            stripePriceId: data?.stripePriceId || null,
          });
          setStatus("ready");
        }
      } catch (err) {
        console.error("Error fetching product detail:", err);
        if (!cancelled) {
          setError(err.message || "Failed to load product.");
          setStatus("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const fetchPricing = useCallback(async (productId, fallback) => {
    if (!productId) return fallback;
    try {
      setPricingLoading(true);
      const res = await fetch(
        `/.netlify/functions/get-product-pricing?productId=${encodeURIComponent(
          productId
        )}`,
        { credentials: "same-origin" }
      );
      const data = await res.json().catch(() => ({}));
      const merged = {
        price:
          typeof data.price === "number"
            ? data.price
            : fallback?.price ?? null,
        currency: data.currency ?? fallback?.currency ?? "CAD",
        stripePriceId:
          data.stripePriceId ?? fallback?.stripePriceId ?? null,
      };
      setPricing(merged);
      return merged;
    } catch (e) {
      console.warn("pricing fetch failed; using fallback", e);
      setPricing(fallback);
      return fallback;
    } finally {
      setPricingLoading(false);
    }
  }, []);

  const onAddToCart = async () => {
    if (!product) return;
    let currentPricing = pricing;
    if (currentPricing.price == null) {
      currentPricing = await fetchPricing(product._id, {
        price: product.price ?? null,
        currency: product.currency ?? "CAD",
        stripePriceId: product.stripePriceId ?? null,
      });
    }
    const enriched = { ...product, ...currentPricing };
    addToCart(enriched);
  };

  // 1. Unified Loading State
  if (status === "loading") {
    return <LoadingScreen />;
  }

  if (status === "error" || !product) {
    return (
      <section className="pdp-container">
        <div className="pdp-layout">
          <h2 className="pdp-title">{error || "Not found."}</h2>
        </div>
      </section>
    );
  }

  const title = product.title || product.name || "Untitled";
  const mainImage = product.mainImage || product.images?.[0] || null;
  const imgUrl = mainImage ? urlFor(mainImage).width(1200).auto("format").url() : null;
  const displayPrice = (pricing.price ?? product.price);
  const currency = (pricing.currency ?? product.currency ?? "CAD");
  const details = product.details || {};
  const isSoldOut = product.inventory !== null && product.inventory <= 0;

  // Defaults for Details Box
  const format = details.format || "Hardcover";
  const pages = details.pages || "72";
  const dimensions = details.dimensions || "Standard";
  const language = details.language || "English"; // Restored Language

  return (
    <div className="pdp-container">
      <div className="pdp-layout">
        
        {/* LEFT COLUMN: Visuals Only (Gallery) */}
        <div className="pdp-image-col">
          <div className="pdp-image-frame">
            {imgUrl ? (
              <img src={imgUrl} alt={title} className="pdp-image" />
            ) : (
              <div className="pdp-no-image">No Image</div>
            )}
            {isSoldOut && <div className="pdp-sold-out">SOLD OUT</div>}
          </div>
        </div>

        {/* RIGHT COLUMN: Action Center */}
        <div className="pdp-content-col">

          {/* PURCHASE CARD: Identity + Price + Actions */}
          <section className="pdp-card pdp-purchase-card">
            {/* 1. IDENTITY: Title & Subtitle */}
            <div>
              <h1 className="pdp-title">{title}</h1>
              {/* Restored Subtitle */}
              {product.subtitle && (
                <p className="pdp-subtitle" style={{ fontSize: '1.2rem', color: '#666', marginTop: '-5px', marginBottom: '10px' }}>
                  {product.subtitle}
                </p>
              )}
            </div>

            {/* 2. COST & URGENCY: Price + Stock */}
            <div className="pdp-price-row">
              <div className="pdp-price-block">
                <span className="pdp-price-val">
                  {displayPrice != null ? `$${displayPrice.toFixed(2)}` : "---"}
                </span>
                <span className="pdp-currency">{currency}</span>
              </div>
              {/* Inline Stock Indicator */}
              <div className={`pdp-stock-badge ${isSoldOut ? "out" : "in"}`}>
                {isSoldOut ? "Out of Stock" : "In Stock"}
              </div>
            </div>

            {/* 3. THE ACTION: Buy Buttons (The Hot Zone) */}
            <div className="pdp-hot-zone">
              {!isSoldOut ? (
                <button
                  className="pdp-btn-block btn-primary"
                  onClick={onAddToCart}
                  disabled={pricingLoading}
                >
                  {pricingLoading ? "ADDING..." : "ADD TO CART"}
                </button>
              ) : (
                <div className="pdp-btn-block btn-disabled">UNAVAILABLE</div>
              )}

              {!isSoldOut && (
                <div className="pdp-checkout-wrapper">
                  <CheckoutButton productId={product._id} />
                </div>
              )}
            </div>
            
            {/* Restored SKU */}
            {product.sku && (
              <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#999', marginTop: '5px' }}>
                SKU: {product.sku}
              </div>
            )}
          </section>

          {/* DETAILS CARD: Pitch + Specs */}
          <section className="pdp-card pdp-details-card">
            {/* 4. THE PITCH: Description */}
            <div className="pdp-cutout-box">
              <div className="scissors-icon" aria-hidden="true">
                ✂️
              </div>
              <div className="pdp-description">
                {product.description ? (
                  <PortableText value={product.description} />
                ) : (
                  <p>No description available.</p>
                )}
              </div>
            </div>

            {/* 5. THE LOGIC: Book Details */}
            <div className="specs-container">
              <div className="specs-header">Book Details</div>
              <div className="specs-bar-thick"></div>

              <div className="specs-row">
                <span>Format</span>
                <strong>{format}</strong>
              </div>
              <div className="specs-bar-thin"></div>

              <div className="specs-row">
                <span>Pages</span>
                <strong>{pages}</strong>
              </div>
              <div className="specs-bar-thin"></div>

              <div className="specs-row">
                <span>Dimensions</span>
                <strong>{dimensions}</strong>
              </div>
              
              {/* Restored Language Row */}
              <div className="specs-bar-thin"></div>
              <div className="specs-row">
                <span>Language</span>
                <strong>{language}</strong>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default ProductDetailPage;